import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FINDING_CLASSIFICATIONS,
  FINDING_CLASSIFICATION_DEFINITIONS,
  FINDING_CLASSIFICATION_LABELS,
  FINDING_STATUSES,
  FINDING_STATUS_LABELS,
  type CreateFindingInput,
  type FindingClassification,
  type FindingStatus,
  type InternalAuditFinding,
  type UpdateFindingInput,
} from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { Badge, Button, Field, Input, Select, Textarea, formatDate, useToast } from '../../components/ui.js';
import {
  ClassificationBadge,
  FindingStatusBadge,
  auditKey,
  errorMessage,
  issuesOf,
  itemsKey,
} from './shared.js';

interface Draft {
  classification: FindingClassification;
  title: string;
  finding: string;
  evidence: string;
  recommendation: string;
  status: FindingStatus;
  decisionNote: string;
  auditeeResponse: string;
}

function draftOf(finding: InternalAuditFinding | null): Draft {
  return {
    classification: finding?.classification ?? 'minor_nonconformity',
    title: finding?.title ?? '',
    finding: finding?.finding ?? '',
    evidence: finding?.evidence ?? '',
    recommendation: finding?.recommendation ?? '',
    status: finding?.status ?? 'open',
    decisionNote: finding?.decisionNote ?? '',
    auditeeResponse: finding?.auditeeResponse ?? '',
  };
}

const DECISION_HINT =
  'Whether action will be taken and why. Required when a finding is rejected, or an observation or opportunity for improvement is closed without a corrective action.';
const AUDITEE_HINT =
  'The auditee’s view, recorded before the audit closes so factual errors can be corrected.';

/**
 * One finding: read view with Edit / Raise corrective action / Delete, or the
 * inline form. With `finding` null it is the "Add finding" form for `itemId`.
 */
export function FindingEditor({
  auditId,
  itemId,
  finding,
  canEdit,
  onRaise,
  onDelete,
  onCancelNew,
}: {
  auditId: string;
  itemId: string;
  finding: InternalAuditFinding | null;
  canEdit: boolean;
  onRaise: (finding: InternalAuditFinding) => void;
  onDelete: (finding: InternalAuditFinding) => void;
  /** Create mode only: the form was dismissed without saving. */
  onCancelNew?: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isNew = finding === null;
  const [editing, setEditing] = useState(isNew);
  const [draft, setDraft] = useState<Draft>(() => draftOf(finding));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: itemsKey(auditId) });
    void queryClient.invalidateQueries({ queryKey: auditKey(auditId) });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) {
        const body: CreateFindingInput = {
          itemId,
          classification: draft.classification,
          title: draft.title.trim(),
          finding: draft.finding,
          evidence: draft.evidence,
          recommendation: draft.recommendation,
        };
        return apiFetch<InternalAuditFinding>(`/internal-audits/${auditId}/findings`, { method: 'POST', body });
      }
      const body: UpdateFindingInput = {};
      if (draft.classification !== finding.classification) body.classification = draft.classification;
      if (draft.title.trim() !== finding.title) body.title = draft.title.trim();
      if (draft.finding !== finding.finding) body.finding = draft.finding;
      if (draft.evidence !== finding.evidence) body.evidence = draft.evidence;
      if (draft.recommendation !== finding.recommendation) body.recommendation = draft.recommendation;
      if (draft.decisionNote !== finding.decisionNote) body.decisionNote = draft.decisionNote;
      if (draft.auditeeResponse !== finding.auditeeResponse) body.auditeeResponse = draft.auditeeResponse;
      if (draft.status !== finding.status) body.status = draft.status;
      if (Object.keys(body).length === 0) return finding;
      return apiFetch<InternalAuditFinding>(`/internal-audits/${auditId}/findings/${finding.id}`, {
        method: 'PATCH',
        body,
      });
    },
    onSuccess: () => {
      refresh();
      toast(isNew ? 'Finding recorded.' : 'Finding saved.', 'success');
      setErrors({});
      if (isNew) onCancelNew?.();
      else setEditing(false);
    },
    onError: (err) => {
      setErrors(issuesOf(err));
      toast(errorMessage(err, 'Could not save the finding'), 'error');
    },
  });

  const startEdit = () => {
    setDraft(draftOf(finding));
    setErrors({});
    setEditing(true);
  };
  const cancel = () => {
    if (isNew) onCancelNew?.();
    else {
      setEditing(false);
      setErrors({});
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (draft.title.trim() === '' || save.isPending) return;
    save.mutate();
  };

  if (editing) {
    return (
      <form className="finding finding-edit" onSubmit={submit} aria-label={isNew ? 'New finding' : `Edit finding ${finding.title}`}>
        <div className="audit-form-row">
          <Field
            label="Classification"
            required
            hint={FINDING_CLASSIFICATION_DEFINITIONS[draft.classification]}
            error={errors.classification}
          >
            {(props) => (
              <Select
                {...props}
                value={draft.classification}
                onChange={(e) => set('classification', e.target.value as FindingClassification)}
              >
                {FINDING_CLASSIFICATIONS.map((c) => (
                  <option key={c} value={c}>
                    {FINDING_CLASSIFICATION_LABELS[c]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {!isNew && (
            <Field label="Status" error={errors.status}>
              {(props) => (
                <Select {...props} value={draft.status} onChange={(e) => set('status', e.target.value as FindingStatus)}>
                  {FINDING_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {FINDING_STATUS_LABELS[s]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
        </div>
        <Field label="Title" required error={errors.title}>
          {(props) => (
            <Input {...props} required maxLength={200} value={draft.title} onChange={(e) => set('title', e.target.value)} />
          )}
        </Field>
        <Field label="Finding" hint="What was observed, against which requirement" error={errors.finding}>
          {(props) => (
            <Textarea {...props} rows={3} maxLength={2000} value={draft.finding} onChange={(e) => set('finding', e.target.value)} />
          )}
        </Field>
        <Field label="Objective evidence" hint="The records, documents or responses that support the finding" error={errors.evidence}>
          {(props) => (
            <Textarea {...props} rows={2} maxLength={2000} value={draft.evidence} onChange={(e) => set('evidence', e.target.value)} />
          )}
        </Field>
        <Field label="Recommendation" error={errors.recommendation}>
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              maxLength={2000}
              value={draft.recommendation}
              onChange={(e) => set('recommendation', e.target.value)}
            />
          )}
        </Field>
        {!isNew && (
          <>
            <Field label="Decision note" hint={DECISION_HINT} error={errors.decisionNote}>
              {(props) => (
                <Textarea
                  {...props}
                  rows={2}
                  maxLength={2000}
                  value={draft.decisionNote}
                  onChange={(e) => set('decisionNote', e.target.value)}
                />
              )}
            </Field>
            <Field label="Auditee comments" hint={AUDITEE_HINT} error={errors.auditeeResponse}>
              {(props) => (
                <Textarea
                  {...props}
                  rows={2}
                  maxLength={2000}
                  value={draft.auditeeResponse}
                  onChange={(e) => set('auditeeResponse', e.target.value)}
                />
              )}
            </Field>
          </>
        )}
        <div className="dialog-actions" style={{ marginTop: 0 }}>
          <Button variant="secondary" size="sm" onClick={cancel} disabled={save.isPending}>
            Cancel
          </Button>
          <Button type="submit" size="sm" loading={save.isPending} disabled={draft.title.trim() === ''}>
            {isNew ? 'Add finding' : 'Save finding'}
          </Button>
        </div>
      </form>
    );
  }

  if (isNew) return null;
  const canRaise = finding.classification !== 'conforming' && !finding.correctiveActionRecordId;
  const recordDeleted = finding.correctiveActionName === '(record deleted)';

  return (
    <article className="finding" aria-label={finding.title}>
      <div className="finding-head">
        <ClassificationBadge classification={finding.classification} />
        <FindingStatusBadge status={finding.status} />
        {finding.aiGenerated && <Badge tone="purple">AI-drafted</Badge>}
      </div>
      <h4 className="finding-title">{finding.title}</h4>
      <dl className="finding-fields">
        {finding.finding && (
          <>
            <dt>Finding</dt>
            <dd className="audit-pre">{finding.finding}</dd>
          </>
        )}
        {finding.evidence && (
          <>
            <dt>Objective evidence</dt>
            <dd className="audit-pre">{finding.evidence}</dd>
          </>
        )}
        {finding.recommendation && (
          <>
            <dt>Recommendation</dt>
            <dd className="audit-pre">{finding.recommendation}</dd>
          </>
        )}
        {finding.decisionNote && (
          <>
            <dt>Decision</dt>
            <dd className="audit-pre">{finding.decisionNote}</dd>
          </>
        )}
        {finding.auditeeResponse && (
          <>
            <dt>Auditee comments</dt>
            <dd className="audit-pre">{finding.auditeeResponse}</dd>
          </>
        )}
        {finding.correctiveActionRecordId && (
          <>
            <dt>Corrective action</dt>
            <dd>
              {recordDeleted ? (
                <span className="audit-muted">(record deleted)</span>
              ) : (
                <Link
                  to={`/registers/corrective-actions?record=${encodeURIComponent(finding.correctiveActionRecordId)}`}
                >
                  {finding.correctiveActionName ?? 'Open corrective action'}
                </Link>
              )}
            </dd>
          </>
        )}
      </dl>
      <div className="finding-meta">
        {finding.createdByName ? `Recorded by ${finding.createdByName}` : 'Drafted by the AI auditor'} ·{' '}
        {formatDate(finding.createdAt)}
        {finding.updatedAt !== finding.createdAt && ` · edited ${formatDate(finding.updatedAt)}`}
      </div>
      {canEdit && (
        <div className="finding-actions">
          <Button size="sm" variant="secondary" onClick={startEdit}>
            Edit
          </Button>
          {canRaise && (
            <Button size="sm" variant="secondary" onClick={() => onRaise(finding)}>
              Raise corrective action
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onDelete(finding)}>
            Delete
          </Button>
        </div>
      )}
    </article>
  );
}
